import React, { useState, useEffect, useRef } from 'react';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, sendEmailVerification, signOut } from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, addDoc, collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { auth, db, functions } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';

const AdminLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [isFirstTime, setIsFirstTime] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [twoFactorCodeSent, setTwoFactorCodeSent] = useState(false);
  const navigate = useNavigate();

  const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL || 'mail@mindfulpracticebuddy.com.au';

  useEffect(() => {
    // Component cleanup on unmount
    return () => {
      // No special cleanup needed for email 2FA
    };
  }, []);

  const checkIfFirstTime = async () => {
    try {
      // Check if admin config document exists (regardless of authentication status)
      const adminDoc = await getDoc(doc(db, 'admin', 'config'));
      return !adminDoc.exists();
    } catch (error) {
      console.error('Error checking admin config:', error);
      // If we can't access admin config due to permissions, assume it's not first time
      // This prevents the error from blocking the login flow
      return false;
    }
  };

  const check2FAStatus = async () => {
    try {
      const adminDoc = await getDoc(doc(db, 'admin', 'config'));
      if (adminDoc.exists()) {
        return adminDoc.data().twoFactorEnabled || false;
      }
      return true; // Default to 2FA enabled if no config exists
    } catch (error) {
      console.error('Error checking 2FA status:', error);
      // If we can't access admin config due to permissions, default to 2FA enabled
      return true;
    }
  };

  const generateVerificationCode = (): string => {
    // Generate a 6-digit verification code
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  const sendEmail2FA = async () => {
    try {
      setLoading(true);
      setError('');

      // Generate a 6-digit verification code
      const verificationCode = generateVerificationCode();
      
      // Store the verification code in Firestore with expiration
      const codeData = {
        code: verificationCode,
        email: ADMIN_EMAIL,
        createdAt: new Date(),
        expiresAt: new Date(Date.now() + 10 * 60 * 1000), // 10 minutes from now
        used: false
      };

      // Add to verification codes collection
      await addDoc(collection(db, 'verificationCodes'), codeData);

      // Send email using Firebase Function
      try {
        const sendEmail = httpsCallable(functions, 'sendAdminVerificationEmail');
        const result = await sendEmail({
          email: ADMIN_EMAIL,
          code: verificationCode,
          adminEmail: ADMIN_EMAIL
        });
        
        // Security: Removed email function result logging
        
        const responseData = result.data as any;
        if (responseData?.success) {
          if (responseData.code) {
            // SendGrid not configured, show code in console
            console.log('2FA Verification Code (SendGrid not configured):', responseData.code);
          } else {
            console.log('Verification email sent successfully');
          }
        }
      } catch (emailError: any) {
        console.error('Failed to send email, but code is stored:', emailError);
        // Don't fail the entire process if email sending fails
        // The code is still stored and can be retrieved from console
        console.log('2FA Verification Code (fallback):', verificationCode);
      }
      
      setTwoFactorCodeSent(true);
      setShow2FA(true);
      
    } catch (error: any) {
      console.error('Email 2FA error:', error);
      setError('Failed to send 2FA code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyEmail2FA = async () => {
    try {
      setLoading(true);
      setError('');

      if (!verificationCode || verificationCode.length !== 6) {
        setError('Please enter a valid 6-digit verification code.');
        return;
      }

      // Check if the verification code exists and is valid
      const codesRef = collection(db, 'verificationCodes');
      const q = query(
        codesRef,
        where('code', '==', verificationCode)
      );

      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        setError('Invalid verification code. Please try again.');
        return;
      }

      // Find the most recent unused code for the correct email
      let codeDoc: any = null;
      let latestDate = new Date(0);
      
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        // Check email and used status in client-side filtering
        if (data.email === ADMIN_EMAIL && !data.used) {
          const createdAt = data.createdAt.toDate();
          if (createdAt > latestDate) {
            latestDate = createdAt;
            codeDoc = doc;
          }
        }
      });

      if (!codeDoc) {
        setError('Invalid verification code. Please try again.');
        return;
      }

      const codeData = codeDoc.data();
      
      // Check if code has expired
      if (new Date() > codeData.expiresAt.toDate()) {
        setError('Verification code has expired. Please request a new one.');
        return;
      }

      // Mark code as used
      await updateDoc(doc(db, 'verificationCodes', codeDoc.id), {
        used: true,
        usedAt: new Date()
      });

      // Create admin user document if it doesn't exist
      try {
        if (auth.currentUser) {
          const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
          if (!userDoc.exists()) {
            await setDoc(doc(db, 'users', auth.currentUser.uid), {
              email: ADMIN_EMAIL,
              role: 'admin',
              name: 'Admin',
              nickname: 'Admin',
              createdAt: new Date(),
              emailVerified: true // Admin email is pre-verified
            });
            console.log('Created admin user document in Firestore');
          } else {
            console.log('Admin user document already exists');
          }
        }
      } catch (userDocError) {
        console.error('Could not create admin user document:', userDocError);
        setError('Failed to create admin user document. Please try again.');
        return;
      }

      // Update admin config to mark 2FA as completed
      try {
        await updateDoc(doc(db, 'admin', 'config'), {
          twoFactorVerified: true,
          lastLogin: new Date()
        });
      } catch (updateError) {
        console.log('Could not update admin config after 2FA, proceeding anyway:', updateError);
      }

      navigate('/addminn/dashboard');
    } catch (error: any) {
      console.error('Email 2FA verification error:', error);
      setError('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resendEmailVerification = async () => {
    if (!auth.currentUser) return;

    try {
      setLoading(true);
      setError('');
      await sendEmailVerification(auth.currentUser);
      setError('');
      setLoading(false);
      // Show success message
      setError('Verification email sent! Please check your inbox.');
      setTimeout(() => setError(''), 3000);
    } catch (error: any) {
      console.error('Error resending verification email:', error);
      setError('Failed to resend verification email. Please try again.');
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      console.log('=== LOGIN FLOW START ===');
      // Security: Don't log sensitive information
      
      // Check if this is the first time setup
      const isFirstTimeSetup = await checkIfFirstTime();
      // Security: Removed sensitive login attempt logging

      if (email !== ADMIN_EMAIL) {
        setError('Access denied. Invalid admin email.');
        setLoading(false);
        return;
      }

      if (isFirstTimeSetup) {
        // First time setup - create account
        if (password.length < 8) {
          setError('Password must be at least 8 characters long.');
          setLoading(false);
          return;
        }

        try {
          // Create admin account
          const userCredential = await createUserWithEmailAndPassword(auth, email, password);
          
          // Send email verification
          await sendEmailVerification(userCredential.user);
          setEmailVerificationSent(true);
          
          // Create admin user document first (before AuthContext tries to fetch it)
          await setDoc(doc(db, 'users', userCredential.user.uid), {
            email: email,
            role: 'admin',
            name: 'Admin',
            nickname: 'Admin',
            createdAt: new Date(),
            emailVerified: false
          });

          // Create admin config document
          await setDoc(doc(db, 'admin', 'config'), {
            email: email,
            createdAt: new Date(),
            lastLogin: new Date(),
            twoFactorEnabled: true,
            twoFactorVerified: false
          });

          // After account creation, proceed to 2FA setup (if email is verified)
          if (userCredential.user.emailVerified) {
            await sendEmail2FA();
          } else {
            // Show email verification message first
            setError('');
            setLoading(false);
          }
          return;
        } catch (createError: any) {
          console.error('Admin account creation error:', createError);
          if (createError.code === 'auth/email-already-in-use') {
            // Account exists but might be missing user document, try to sign in
            try {
              // Try to sign in - this might trigger MFA
              await signInWithEmailAndPassword(auth, email, password);
              
              // If we get here, sign in was successful
              // Check if user document exists, create if missing
              if (auth.currentUser) {
                const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
                if (!userDoc.exists()) {
                  // Create missing user document for existing admin
                  await setDoc(doc(db, 'users', auth.currentUser.uid), {
                    email: email,
                    role: 'admin',
                    name: 'Admin',
                    nickname: 'Admin',
                    createdAt: new Date()
                  });
                }
                
                // Check if admin config exists, create if missing
                try {
                  const adminDoc = await getDoc(doc(db, 'admin', 'config'));
                  if (!adminDoc.exists()) {
                    await setDoc(doc(db, 'admin', 'config'), {
                      email: email,
                      createdAt: new Date(),
                      lastLogin: new Date(),
                      twoFactorEnabled: true,
                      twoFactorVerified: false
                    });
                  }
                } catch (adminConfigError) {
                  console.log('Could not access admin config, proceeding anyway:', adminConfigError);
                }
                
                // Check if 2FA is enabled and proceed accordingly
                const twoFactorEnabled = await check2FAStatus();
                
                if (twoFactorEnabled) {
                  await sendEmail2FA();
                } else {
                  navigate('/addminn/dashboard');
                }
              }
              return;
            } catch (signInError: any) {
              console.error('Sign in error:', signInError);
              if (signInError.code === 'auth/multi-factor-auth-required') {
                // MFA is required, proceed to email 2FA verification
                try {
                  await sendEmail2FA();
                  return;
                } catch (email2FAError: any) {
                  console.error('Email 2FA error:', email2FAError);
                  setError('Multi-factor authentication is required. Please contact system administrator.');
                }
              } else {
                setError('Admin account exists but login failed. Please check your password.');
              }
            }
          } else {
            setError(`Failed to create admin account: ${createError.message}`);
          }
          setLoading(false);
          return;
        }
      } else {
        // Regular login
        try {
          await signInWithEmailAndPassword(auth, email, password);
          
          // Check if user document exists, create if missing
          if (auth.currentUser) {
            const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
            if (!userDoc.exists()) {
              // Create missing user document for existing admin
              await setDoc(doc(db, 'users', auth.currentUser.uid), {
                email: email,
                role: 'admin',
                name: 'Admin',
                nickname: 'Admin',
                createdAt: new Date()
              });
            }
          }
          
          // Refresh user to get latest email verification status
          if (auth.currentUser) {
            await auth.currentUser.reload();
            // Email verified status after reload
          }
          
          // Send email verification for existing account
          if (auth.currentUser && !auth.currentUser.emailVerified) {
            console.log('Email not verified, sending verification email...');
            await sendEmailVerification(auth.currentUser);
            setEmailVerificationSent(true);
            setLoading(false);
            return;
          }

          // Check if 2FA is enabled
          const twoFactorEnabled = await check2FAStatus();
          
          if (twoFactorEnabled) {
            // Proceed to email 2FA verification
            await sendEmail2FA();
            return;
          } else {
            // Update last login and proceed to dashboard
            try {
              await updateDoc(doc(db, 'admin', 'config'), {
                lastLogin: new Date()
              });
            } catch (updateError) {
              console.log('Could not update admin config, proceeding anyway:', updateError);
            }
            navigate('/addminn/dashboard');
          }
        } catch (loginError: any) {
          console.error('Login error:', loginError);
          if (loginError.code === 'auth/multi-factor-auth-required') {
            // MFA is required, proceed to email 2FA verification
            try {
              await sendEmail2FA();
              return;
            } catch (email2FAError: any) {
              console.error('Email 2FA error:', email2FAError);
              setError('Multi-factor authentication is required. Please contact system administrator.');
            }
          } else if (loginError.code === 'auth/user-not-found') {
            setError('Admin account not found. Please check your email or contact support.');
          } else if (loginError.code === 'auth/wrong-password') {
            setError('Incorrect password. Please try again.');
          } else if (loginError.code === 'auth/invalid-email') {
            setError('Invalid email address.');
          } else {
            setError('Login failed. Please try again.');
          }
          setLoading(false);
          return;
        }
      }
    } catch (error: any) {
      console.error('Admin login error:', error);
      if (error.code === 'auth/user-not-found') {
        setError('Admin account not found. Please contact system administrator.');
      } else if (error.code === 'auth/wrong-password') {
        setError('Incorrect password.');
      } else if (error.code === 'auth/email-already-in-use') {
        setError('Admin account already exists. Please log in instead.');
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setShow2FA(false);
    setVerificationCode('');
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-red-900 to-black flex items-center justify-center">
      <div className="max-w-md w-full space-y-8 p-8">
        <div className="text-center">
                  <h2 className="mt-6 text-3xl font-extrabold text-white">
                    {emailVerificationSent ? 'Email Verification Sent' : 
                     show2FA ? 'Email 2FA Verification' : 'Admin Access'}
                  </h2>
                  <p className="mt-2 text-sm text-gray-300">
                    {emailVerificationSent 
                      ? 'Please check your email and click the verification link'
                      : show2FA 
                        ? 'Enter the 6-digit verification code sent to your email'
                        : 'Restricted Area - Authorized Personnel Only'
                    }
                  </p>
        </div>
        
        {emailVerificationSent ? (
          <div className="mt-8 space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Verification Email Sent
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>
                      We've sent a verification email to <strong>{email}</strong>. 
                      Please check your inbox and click the verification link to complete your admin account setup.
                    </p>
                  </div>
                  <div className="mt-4 space-y-2">
                    <button
                      type="button"
                      onClick={resendEmailVerification}
                      disabled={loading}
                      className="text-sm font-medium text-blue-600 hover:text-blue-500 disabled:opacity-50"
                    >
                      {loading ? 'Sending...' : 'Resend verification email'}
                    </button>
                    <div>
                      <button
                        type="button"
                        onClick={async () => {
                          if (auth.currentUser) {
                            await auth.currentUser.reload();
                            // Email verified status checked
                            if (auth.currentUser.emailVerified) {
                              setEmailVerificationSent(false);
                              setError('Email verified! You can now proceed with login.');
                            } else {
                              setError('Email still not verified. Please check your email and click the verification link.');
                            }
                          }
                        }}
                        className="text-sm font-medium text-green-600 hover:text-green-500"
                      >
                        Check if email is verified
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setEmailVerificationSent(false);
                  setEmail('');
                  setPassword('');
                  setConfirmPassword('');
                }}
                className="text-sm text-gray-300 hover:text-white"
              >
                Back to login
              </button>
            </div>
          </div>
        ) : show2FA ? (
          <form className="mt-8 space-y-6" onSubmit={(e) => { e.preventDefault(); verifyEmail2FA(); }}>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Email 2FA Verification
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>
                      A 6-digit verification code has been sent to <strong>{ADMIN_EMAIL}</strong>. 
                      Please check your email and enter the code below.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-md shadow-sm">
              <div>
                <label htmlFor="verificationCode" className="sr-only">
                  Verification Code
                </label>
                <input
                  id="verificationCode"
                  name="verificationCode"
                  type="text"
                  autoComplete="one-time-code"
                  required
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-red-500 focus:border-red-500 focus:z-10 sm:text-sm"
                  placeholder="Enter 6-digit verification code"
                  maxLength={6}
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            <div className="flex space-x-4">
              <button
                type="button"
                onClick={handleBackToLogin}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-md text-sm font-medium transition-colors"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading || verificationCode.length !== 6}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded-md text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Verifying...' : 'Verify Code'}
              </button>
            </div>

            <div className="text-center space-y-2">
              <button
                type="button"
                onClick={sendEmail2FA}
                className="text-sm text-gray-300 hover:text-white underline"
                disabled={loading}
              >
                Resend Verification Code
              </button>
              <p className="text-xs text-gray-400">
                Verification codes expire after 10 minutes
              </p>
            </div>
          </form>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="email" className="sr-only">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-red-500 focus:border-red-500 focus:z-10 sm:text-sm"
                  placeholder="Admin email address"
                />
              </div>
              <div>
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-red-500 focus:border-red-500 focus:z-10 sm:text-sm"
                  placeholder="Password"
                />
              </div>
              {false && ( // Temporarily disable confirm password field
                <div>
                  <label htmlFor="confirmPassword" className="sr-only">
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-red-500 focus:border-red-500 focus:z-10 sm:text-sm"
                    placeholder="Confirm password"
                  />
                </div>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            <div>
              <button
                type="submit"
                disabled={loading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Processing...' : (isFirstTime ? 'Create Admin Account' : 'Sign In')}
              </button>
            </div>

          </form>
        )}

        <div className="text-center">
          <p className="text-xs text-gray-400">
            This is a restricted administrative area. All access attempts are logged.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
